import React, { useEffect, useState, useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useSnackbar } from 'react-simple-snackbar'
import { useLocation } from 'react-router-dom'
import { useDropzone } from 'react-dropzone'
import { getProfilesByUser, updateProfile, createProfile } from '../../../actions/profile'
import { isAdmin } from '../../../utils/auth'
import styles from './SettingsForm.module.css'

const DEFAULT_PROFILE = {
  businessName: 'California Gym',
  contactAddress: '',
  phoneNumber: '',
  email: '',
  logo: '/cg-logo.png',
  paymentDetails: '',
  name: '',
}

const IconBuilding = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="4" y="2" width="16" height="20" rx="2" />
    <path d="M9 22v-4h6v4" />
    <path d="M8 6h.01M16 6h.01M12 6h.01M8 10h.01M16 10h.01M12 10h.01M8 14h.01M16 14h.01M12 14h.01" />
  </svg>
)

const IconPin = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
)

const IconPhone = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
)

const IconMail = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
)

const IconBank = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="1" y="4" width="22" height="16" rx="2" />
    <line x1="1" y1="10" x2="23" y2="10" />
  </svg>
)

const InfoRow = ({ icon, label, value }) => (
  <div className={styles.infoRow}>
    <div className={styles.infoIcon}>{icon}</div>
    <div className={styles.infoContent}>
      <span className={styles.infoLabel}>{label}</span>
      <p className={`${styles.infoValue} ${!value ? styles.infoEmpty : ''}`}>
        {value || 'Non renseigné'}
      </p>
    </div>
  </div>
)

const LogoUploader = ({ form, setForm }) => {
  const [progress, setProgress] = useState(0)

  const onDrop = useCallback((acceptedFiles) => {
    const url = 'https://api.cloudinary.com/v1_1/almpo/image/upload'
    acceptedFiles.forEach(async (file) => {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('upload_preset', 'invoice')
      setProgress(30)
      try {
        const response = await fetch(url, { method: 'post', body: formData })
        const data = await response.json()
        setProgress(100)
        setForm((prev) => ({ ...prev, logo: data.secure_url }))
      } catch {
        setProgress(0)
      }
    })
  }, [setForm])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: 'image/*',
    multiple: false,
  })

  return (
    <div className={styles.formGroupFull}>
      <label>Logo entreprise</label>
      <div className={styles.logoSection} style={{ marginBottom: 12, paddingBottom: 0, border: 'none' }}>
        <div className={styles.logoBox}>
          <img src={form.logo || '/cg-logo.png'} alt="Logo" />
        </div>
      </div>
      <div
        {...getRootProps()}
        className={`${styles.uploadZone} ${isDragActive ? styles.uploadActive : ''}`}
      >
        <input {...getInputProps()} />
        Glissez une image ou cliquez pour changer le logo
        <div className={styles.uploadHint}>PNG, JPG — format horizontal recommandé</div>
      </div>
      {progress > 0 && progress < 100 && (
        <div className={styles.progress}>
          <div className={styles.progressBar} style={{ width: `${progress}%` }} />
        </div>
      )}
    </div>
  )
}

const SettingsForm = ({ user }) => {
  const dispatch = useDispatch()
  const location = useLocation()
  const [openSnackbar] = useSnackbar()
  const { profiles } = useSelector((state) => state.profiles)
  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState(DEFAULT_PROFILE)

  const userId = user?.result?._id || user?.result?.googleId
  const admin = isAdmin()
  const userName = user?.result?.name || 'Utilisateur'
  const userEmail = user?.result?.email || ''
  const initial = userName.charAt(0).toUpperCase()

  useEffect(() => {
    dispatch(getProfilesByUser({ search: userId }))
  }, [dispatch, location, userId])

  useEffect(() => {
    if (profiles) {
      setForm({
        businessName: profiles.businessName || DEFAULT_PROFILE.businessName,
        contactAddress: profiles.contactAddress || '',
        phoneNumber: profiles.phoneNumber || '',
        email: profiles.email || userEmail,
        logo: profiles.logo || DEFAULT_PROFILE.logo,
        paymentDetails: profiles.paymentDetails || '',
        name: profiles.name || userName,
      })
      localStorage.setItem('profileDetail', JSON.stringify({ ...profiles }))
    } else {
      setForm({ ...DEFAULT_PROFILE, email: userEmail, name: userName })
    }
  }, [profiles, userEmail, userName])

  const display = profiles
    ? {
        businessName: profiles.businessName || DEFAULT_PROFILE.businessName,
        contactAddress: profiles.contactAddress,
        phoneNumber: profiles.phoneNumber,
        email: profiles.email || userEmail,
        logo: profiles.logo || DEFAULT_PROFILE.logo,
        paymentDetails: profiles.paymentDetails,
      }
    : { ...DEFAULT_PROFILE, email: userEmail }

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value })

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    try {
      const payload = { ...form, userId, name: userName }
      if (profiles?._id) {
        await dispatch(updateProfile(profiles._id, payload, openSnackbar))
      } else {
        await dispatch(createProfile(payload, null))
        openSnackbar('Profil créé avec succès')
        dispatch(getProfilesByUser({ search: userId }))
      }
      setEditing(false)
    } catch {
      openSnackbar('Erreur lors de la mise à jour')
    } finally {
      setSaving(false)
    }
  }

  const startEdit = () => {
    setForm({
      businessName: display.businessName,
      contactAddress: display.contactAddress || '',
      phoneNumber: display.phoneNumber || '',
      email: display.email || userEmail,
      logo: display.logo,
      paymentDetails: display.paymentDetails || '',
      name: userName,
    })
    setEditing(true)
  }

  return (
    <div className={styles.grid}>
      <section className={styles.card}>
        <div className={styles.cardHeader}>
          <div>
            <h2>Mon compte</h2>
            <p>Informations de connexion</p>
          </div>
        </div>
        <div className={styles.cardBody}>
          <div className={styles.accountHero}>
            <div className={styles.avatar} aria-hidden="true">{initial}</div>
            <div>
              <p className={styles.accountName}>{userName}</p>
              <p className={styles.accountEmail}>{userEmail}</p>
              <span className={`${styles.roleBadge} ${admin ? '' : styles.roleBadgeUser}`}>
                {admin ? 'Administrateur' : 'Utilisateur'}
              </span>
            </div>
          </div>
          <div className={styles.infoList}>
            <InfoRow icon={<IconMail />} label="Email de connexion" value={userEmail} />
            <InfoRow icon={<IconBuilding />} label="Nom affiché" value={userName} />
          </div>
          <p className={styles.note}>
            Pour modifier votre mot de passe ou votre rôle, contactez un administrateur.
          </p>
        </div>
      </section>

      <section className={styles.card}>
        <div className={styles.cardHeader}>
          <div>
            <h2>Profil entreprise</h2>
            <p>Informations affichées sur les documents</p>
          </div>
          {!editing && (
            <button type="button" className={styles.editBtn} onClick={startEdit}>
              Modifier
            </button>
          )}
        </div>

        <div className={styles.cardBody}>
          {!editing ? (
            <>
              <div className={styles.logoSection}>
                <div className={styles.logoBox}>
                  <img
                    src={display.logo}
                    alt={display.businessName}
                  />
                </div>
                <div className={styles.logoMeta}>
                  <h3>{display.businessName}</h3>
                  <p>Logo utilisé sur les factures et documents</p>
                </div>
              </div>
              <div className={styles.infoList}>
                <InfoRow icon={<IconBuilding />} label="Raison sociale" value={display.businessName} />
                <InfoRow icon={<IconPin />} label="Adresse" value={display.contactAddress} />
                <InfoRow icon={<IconPhone />} label="Téléphone" value={display.phoneNumber} />
                <InfoRow icon={<IconMail />} label="Email entreprise" value={display.email} />
                <InfoRow icon={<IconBank />} label="Informations bancaires" value={display.paymentDetails} />
              </div>
            </>
          ) : (
            <form onSubmit={handleSubmit}>
              <LogoUploader form={form} setForm={setForm} />
              <div className={styles.formGrid}>
                <div className={`${styles.formGroup} ${styles.formGroupFull}`}>
                  <label htmlFor="businessName">Raison sociale</label>
                  <input
                    id="businessName"
                    name="businessName"
                    value={form.businessName}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className={`${styles.formGroup} ${styles.formGroupFull}`}>
                  <label htmlFor="contactAddress">Adresse</label>
                  <input
                    id="contactAddress"
                    name="contactAddress"
                    value={form.contactAddress}
                    onChange={handleChange}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label htmlFor="phoneNumber">Téléphone</label>
                  <input
                    id="phoneNumber"
                    name="phoneNumber"
                    value={form.phoneNumber}
                    onChange={handleChange}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label htmlFor="email">Email entreprise</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                  />
                </div>
                <div className={`${styles.formGroup} ${styles.formGroupFull}`}>
                  <label htmlFor="paymentDetails">Informations bancaires / notes</label>
                  <textarea
                    id="paymentDetails"
                    name="paymentDetails"
                    value={form.paymentDetails}
                    onChange={handleChange}
                    rows={3}
                  />
                </div>
              </div>
              <div className={styles.formActions}>
                <button type="submit" className={styles.saveBtn} disabled={saving}>
                  {saving ? 'Enregistrement...' : 'Enregistrer'}
                </button>
                <button
                  type="button"
                  className={styles.cancelBtn}
                  onClick={() => setEditing(false)}
                  disabled={saving}
                >
                  Annuler
                </button>
              </div>
            </form>
          )}
        </div>
      </section>
    </div>
  )
}

export default SettingsForm
